/**
 * app/login/callback/page.tsx — Okta SSO Callback Handler
 *
 * Handles the redirect from Okta after successful authentication.
 * Exchanges authorization code for tokens and redirects to app home.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { handleOktaCallback } from '@/lib/okta';
import { useAuthStore } from '@/lib/store/auth-slice';
import type { User } from '@/lib/types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export default function OktaCallbackPage() {
  const navigate = useNavigate();
  const { setAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function processCallback() {
      try {
        // Handle Okta callback and get tokens
        const result = await handleOktaCallback();
        if (!result) {
          setError('Failed to process Okta callback');
          return;
        }

        const { accessToken } = result;

        // Fetch user info from Django backend using direct fetch
        // IMPORTANT: Don't use the standard API client here because it has
        // 401 refresh logic that will fail for Okta flow and redirect to login
        const res = await fetch(`${API_BASE}/api/v1/auth/me/`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (!res.ok) {
          const errorText = await res.text();
          console.error('API error:', res.status, errorText);
          if (res.status === 401) {
            setError('User not found in system. Please contact administrator to provision your account.');
          } else {
            setError(`Authentication failed: ${errorText || res.statusText}`);
          }
          return;
        }

        const user: User = await res.json();

        // Update auth state with user and Okta token
        setAuth({
          access: accessToken,
          refresh: '', // Okta handles refresh internally
          user,
        });

        // Redirect to intelligence console
        navigate('/console');
      } catch (err) {
        console.error('Okta callback error:', err);
        setError(err instanceof Error ? err.message : 'Authentication failed');
      }
    }

    processCallback();
  }, [navigate, setAuth]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Authentication Error</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-[#1a5f7a] text-white rounded-lg hover:bg-[#154d63] transition-colors"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1a5f7a] mx-auto mb-4"></div>
        <p className="text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
